import { useBreakpointsUp } from '@app/core/hooks/useBreakpointsUp';
import { ListViewInfiniteChildProps } from '@app/core/list-view/ListViewInfinite';
import { useIsActiveStationVideo } from '@app/core/stations/hooks/useIsActiveStationVideo';
import { useFragmentsSettingsStore } from '@app/modules/fragments/stores/fragments-settings.store';
import { FragmentListItemDto } from '@app/modules/fragments/fragment.types';
import { useQueryParam } from '@mi-front/core';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { groupFragmentsByDateSection } from '../utils/group-fragments-by-date-section';
import { FragmentGridContext } from './FragmentGridContext';
import { FragmentsListDateSection } from './FragmentsListDateSection';
import { FragmentsGridSkeleton } from './skeleton/FragmentsGridSkeleton';

export type FragmentsGridProps = ListViewInfiniteChildProps<FragmentListItemDto>;

export function FragmentsGrid({ data, isLoading = false }: FragmentsGridProps) {
  const { $t } = useIntl();
  const breakpointsUp = useBreakpointsUp();
  const isVideo = useIsActiveStationVideo();
  const viewMode = useFragmentsSettingsStore((state) => state.viewMode);

  const { today, yesterday, previous } = useMemo(() => groupFragmentsByDateSection(data), [data]);

  const contextValue = useMemo(
    () => ({ isVideo, breakpointsUp, viewMode }),
    [isVideo, breakpointsUp, viewMode],
  );

  const hasTodayOrYesterday = today.length > 0 || yesterday.length > 0;
  const showPreviousTitle = hasTodayOrYesterday && previous.length > 0;

  const forceSkeleton = useQueryParam('skeleton');
  if (isLoading || forceSkeleton) {
    return (
      <FragmentGridContext.Provider value={contextValue}>
        <FragmentsGridSkeleton />
      </FragmentGridContext.Provider>
    );
  }

  if (!data.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {$t({
            id: 'common.noResults',
            defaultMessage: 'Sin resultados',
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <FragmentGridContext.Provider value={contextValue}>
      <Box>
        <FragmentsListDateSection
          title={$t({
            id: 'fragments.listV3.section.today',
            defaultMessage: 'Hoy',
          })}
          showTitle={today.length > 0}
          fragments={today}
        />
        <FragmentsListDateSection
          title={$t({
            id: 'fragments.listV3.section.yesterday',
            defaultMessage: 'Ayer',
          })}
          showTitle={yesterday.length > 0}
          fragments={yesterday}
        />
        <FragmentsListDateSection
          title={$t({
            id: 'fragments.listV3.section.previous',
            defaultMessage: 'Anteriores',
          })}
          showTitle={showPreviousTitle}
          fragments={previous}
        />
      </Box>
    </FragmentGridContext.Provider>
  );
}
